import {
  AwardRegister,
  BudgetsHistory,
  BudgetsHistoryQuery,
  HeatMap,
  HeatMapQuery,
  InsightsOverview,
  OverviewQuery,
  RegisterQuery,
  SavedView,
  SavedViewList,
  Spend,
  SpendQuery,
  SpotReport,
  SpotReportQuery,
  TeamPeople,
  TeamRecognition,
  TeamRecognitionQuery,
} from '@myrewards/contracts';
import type {
  HeatCells,
  RegisterExportQuery,
  SavedViewCreate,
  SavedViewReport,
  SavedViewUpdate,
  TeamPeopleQuery,
} from '@myrewards/contracts';
import { queryOptions } from '@tanstack/react-query';
import type { z } from 'zod';
import { request } from './client';

// Insights (APP-045): read-only reports. A page's address holds its view (time frame, filters,
// sort, page), with the same names the API uses, so a view can be bookmarked, shared and gone back
// to. Every API query is strict: only the keys below ever reach a request.

const OVERVIEW_KEYS = ['period', 'fiscalYear'] as const;
const REGISTER_FILTER_KEYS = [
  'period',
  'fiscalYear',
  'from',
  'to',
  'type',
  'sectorId',
  'poolId',
  'stage',
  'programId',
  'personId',
  'enteredById',
  'q',
  'costType',
  'lineStatus',
  'sort',
  'groupBy',
] as const;
const REGISTER_KEYS = [...REGISTER_FILTER_KEYS, 'page', 'size'] as const;
const EXPORT_KEYS = [...REGISTER_FILTER_KEYS, 'grain'] as const;
const HEAT_MAP_KEYS = [
  'direction',
  'level',
  'period',
  'fiscalYear',
  'types',
  'sectorId',
  'team',
  'sort',
  'page',
  'size',
  'recognized',
  'q',
  'programId',
  'groupByTeam',
] as const;
const HEAT_EXPORT_KEYS = [
  ...HEAT_MAP_KEYS.filter((k) => k !== 'page' && k !== 'size'),
  'cells',
] as const;
const TEAM_KEYS = ['sectorId', 'period', 'fiscalYear', 'types'] as const;
const TEAM_PEOPLE_KEYS = [
  ...TEAM_KEYS,
  'team',
  'sort',
  'page',
  'size',
  'recognized',
  'q',
  'programId',
  'groupByTeam',
] as const;
const TEAM_EXPORT_KEYS = [
  ...TEAM_PEOPLE_KEYS.filter((k) => k !== 'page' && k !== 'size'),
  'cells',
] as const;
const BUDGETS_KEYS = ['years', 'sectorId'] as const;
const SPEND_KEYS = ['fiscalYear', 'poolId', 'type', 'status', 'costType'] as const;
const SPOT_KEYS = [
  'fiscalYear',
  'sectorId',
  'value',
  'amountFrom',
  'amountTo',
  'nominatedBy',
  'onlyRepeat',
] as const;

/** Values a query has when nothing is chosen: left out of addresses and requests. */
const DEFAULTS: Record<string, string> = {
  period: 'fiscal_year',
  sort: 'newest',
  groupBy: 'none',
  page: '1',
  grain: 'award',
  direction: 'received',
  level: 'sector',
  types: 'all',
  status: 'all',
};
const OVERVIEW_DEFAULTS: Record<string, string> = { ...DEFAULTS, period: '' };
// The heat map's own defaults differ from the register's for two shared names.
const HEAT_MAP_DEFAULTS: Record<string, string> = {
  ...DEFAULTS,
  period: 'last_12_months',
  sort: 'count',
};
// Team recognition's people list is newest first, everyone listed.
const TEAM_DEFAULTS: Record<string, string> = {
  ...HEAT_MAP_DEFAULTS,
  sort: 'recent',
};

type Keys = readonly string[];

/** A view with any of its values left out or cleared (`undefined` means "not chosen"). */
export type Loose<T> = { [K in keyof T]?: T[K] | undefined };

function text(value: unknown): string | null {
  if (value === undefined || value === null) return null;
  if (Array.isArray(value)) return value.length === 0 ? null : value.map(String).join(',');
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return null;
}

/** `?a=1&b=x,y`: only known keys, defaults left out, in a stable order. */
function searchOf(
  query: Readonly<Record<string, unknown>>,
  keys: Keys,
  defaults: Record<string, string> = DEFAULTS,
): string {
  const params = new URLSearchParams();
  for (const key of keys) {
    const value = text(query[key]);
    if (value === null || value === '' || defaults[key] === value) continue;
    params.set(key, value);
  }
  const out = params.toString();
  return out === '' ? '' : `?${out}`;
}

/** The known keys of an address, as the API's parser expects them (repeats become a list). */
function rawOf(search: URLSearchParams, keys: Keys): Record<string, string | string[]> {
  const raw: Record<string, string | string[]> = {};
  for (const key of keys) {
    const values = search.getAll(key).filter((v) => v !== '');
    if (values.length === 1 && values[0] !== undefined) raw[key] = values[0];
    else if (values.length > 1) raw[key] = values;
  }
  return raw;
}

/**
 * Reads a view from an address with the API's own schema. A value that doesn't parse is dropped
 * (a stale bookmark still opens the report); if the rest still doesn't parse, the defaults.
 */
function parseWith<S extends z.ZodType>(
  schema: S,
  search: URLSearchParams,
  keys: Keys,
): z.output<S> {
  const raw = rawOf(search, keys);
  const first = schema.safeParse(raw);
  if (first.success) return first.data;
  const bad = new Set(first.error.issues.map((issue) => String(issue.path[0] ?? '')));
  // A custom range missing an end is reported on `from`/`to`: drop the range as a whole.
  if (bad.has('from') || bad.has('to')) ['period', 'from', 'to'].forEach((k) => bad.add(k));
  const second = schema.safeParse(
    Object.fromEntries(Object.entries(raw).filter(([key]) => !bad.has(key))),
  );
  return second.success ? second.data : schema.parse({});
}

// --- Views from an address ------------------------------------------------------------------------

export const overviewView = (search: URLSearchParams): OverviewQuery =>
  parseWith(OverviewQuery, search, OVERVIEW_KEYS);
export const registerView = (search: URLSearchParams): RegisterQuery =>
  parseWith(RegisterQuery, search, REGISTER_KEYS);
export const heatMapView = (search: URLSearchParams): HeatMapQuery =>
  parseWith(HeatMapQuery, search, HEAT_MAP_KEYS);
export const budgetsView = (search: URLSearchParams): BudgetsHistoryQuery =>
  parseWith(BudgetsHistoryQuery, search, BUDGETS_KEYS);
export const spendView = (search: URLSearchParams): SpendQuery =>
  parseWith(SpendQuery, search, SPEND_KEYS);
export const spotView = (search: URLSearchParams): SpotReportQuery =>
  parseWith(SpotReportQuery, search, SPOT_KEYS);
export const teamView = (search: URLSearchParams): TeamRecognitionQuery =>
  parseWith(TeamRecognitionQuery, search, TEAM_KEYS);

// --- Addresses from a view ------------------------------------------------------------------------

export const overviewSearch = (query: OverviewQuery): string =>
  searchOf(query, OVERVIEW_KEYS, OVERVIEW_DEFAULTS);
export const registerSearch = (query: Loose<RegisterQuery>): string =>
  searchOf(query, REGISTER_KEYS);
export const heatMapSearch = (query: Loose<HeatMapQuery>): string =>
  searchOf(query, HEAT_MAP_KEYS, HEAT_MAP_DEFAULTS);
export const budgetsSearch = (query: BudgetsHistoryQuery): string => searchOf(query, BUDGETS_KEYS);
export const spendSearch = (query: Loose<SpendQuery>): string => searchOf(query, SPEND_KEYS);
export const spotSearch = (query: Loose<SpotReportQuery>): string => searchOf(query, SPOT_KEYS);
export const teamSearch = (query: Loose<TeamRecognitionQuery>): string =>
  searchOf(query, TEAM_KEYS, TEAM_DEFAULTS);
export const teamPeopleSearch = (query: Loose<TeamPeopleQuery>): string =>
  searchOf(query, TEAM_PEOPLE_KEYS, TEAM_DEFAULTS);

/**
 * The CSV of a register view, every page (`GET /api/v1/insights/awards/export`): a plain download
 * link, never a fetch. The row count to show is the register's own total for the same view.
 */
export function registerExportHref(
  query: Loose<RegisterExportQuery>,
  grain: RegisterExportQuery['grain'],
): string {
  // Paging belongs to the screen; the export is the whole view.
  return `/api/v1/insights/awards/export${searchOf({ ...query, grain }, EXPORT_KEYS)}`;
}

/** Every table on the overview as CSV (`GET /api/v1/insights/overview/export`). */
export const overviewExportHref = (query: OverviewQuery): string =>
  `/api/v1/insights/overview/export${searchOf(query, OVERVIEW_KEYS)}`;

/** The heat map's view as CSV, with what its cells show (`…/heat-map/export`). */
export const heatMapExportHref = (query: Loose<HeatMapQuery>, cells: HeatCells): string =>
  `/api/v1/insights/heat-map/export${searchOf({ ...query, cells }, HEAT_EXPORT_KEYS, {
    ...HEAT_MAP_DEFAULTS,
    cells: 'count',
  })}`;

/** Everyone in the sector as the list shows them, as CSV (`…/team/people/export`). */
export const teamPeopleExportHref = (query: Loose<TeamPeopleQuery>, cells: HeatCells): string =>
  `/api/v1/insights/team/people/export${searchOf({ ...query, cells }, TEAM_EXPORT_KEYS, {
    ...TEAM_DEFAULTS,
    cells: 'value',
  })}`;

/** RPT4's list as CSV (`GET /api/v1/insights/spot/export`): the year with the list's filters. */
export const spotExportHref = (query: Loose<SpotReportQuery>): string =>
  `/api/v1/insights/spot/export${searchOf(query, SPOT_KEYS)}`;

/** Every table on Budgets & history as CSV (`…/budgets/export`). */
export const budgetsExportHref = (query: BudgetsHistoryQuery): string =>
  `/api/v1/insights/budgets/export${searchOf(query, BUDGETS_KEYS)}`;

// --- Server calls ---------------------------------------------------------------------------------

export const insightsKeys = {
  all: ['insights'] as const,
  report: (report: string, search: string) => ['insights', report, search] as const,
};

/** Reports change only as awards move; a minute keeps tab switches instant without going stale. */
const STALE = 60_000;

export function overviewQuery(query: OverviewQuery = {}) {
  const search = overviewSearch(query);
  return queryOptions({
    queryKey: insightsKeys.report('overview', search),
    queryFn: ({ signal }) =>
      request(`/api/v1/insights/overview${search}`, { schema: InsightsOverview, signal }),
    staleTime: STALE,
  });
}

/**
 * A request leaves out the starting page size too, so a view asked for at the table's own size is
 * one cache entry whichever way it was reached. (Addresses keep an explicit size.)
 */
const STARTING_SIZE = { size: '25' };

export function registerQuery(query: Loose<RegisterQuery>) {
  const search = searchOf(query, REGISTER_KEYS, { ...DEFAULTS, ...STARTING_SIZE });
  return queryOptions({
    queryKey: insightsKeys.report('awards', search),
    queryFn: ({ signal }) =>
      request(`/api/v1/insights/awards${search}`, { schema: AwardRegister, signal }),
    staleTime: STALE,
  });
}

export function heatMapQuery(query: Loose<HeatMapQuery>) {
  const search = searchOf(query, HEAT_MAP_KEYS, { ...HEAT_MAP_DEFAULTS, ...STARTING_SIZE });
  return queryOptions({
    queryKey: insightsKeys.report('heat-map', search),
    queryFn: ({ signal }) =>
      request(`/api/v1/insights/heat-map${search}`, { schema: HeatMap, signal }),
    staleTime: STALE,
  });
}

export function budgetHistoryQuery(query: BudgetsHistoryQuery) {
  const search = budgetsSearch(query);
  return queryOptions({
    queryKey: insightsKeys.report('budgets', search),
    queryFn: ({ signal }) =>
      request(`/api/v1/insights/budgets${search}`, { schema: BudgetsHistory, signal }),
    staleTime: STALE,
  });
}

export function spendQuery(query: Loose<SpendQuery>) {
  const search = spendSearch(query);
  return queryOptions({
    queryKey: insightsKeys.report('spend', search),
    queryFn: ({ signal }) => request(`/api/v1/insights/spend${search}`, { schema: Spend, signal }),
    staleTime: STALE,
  });
}

export function spotQuery(query: SpotReportQuery = {}) {
  const search = spotSearch(query);
  return queryOptions({
    queryKey: insightsKeys.report('spot', search),
    queryFn: ({ signal }) =>
      request(`/api/v1/insights/spot${search}`, { schema: SpotReport, signal }),
    staleTime: STALE,
  });
}

export function teamQuery(query: Loose<TeamRecognitionQuery>) {
  const search = teamSearch(query);
  return queryOptions({
    queryKey: insightsKeys.report('team', search),
    queryFn: ({ signal }) =>
      request(`/api/v1/insights/team${search}`, { schema: TeamRecognition, signal }),
    staleTime: STALE,
  });
}

export function teamPeopleQuery(query: Loose<TeamPeopleQuery>) {
  const search = searchOf(query, TEAM_PEOPLE_KEYS, { ...TEAM_DEFAULTS, ...STARTING_SIZE });
  return queryOptions({
    queryKey: insightsKeys.report('team-people', search),
    queryFn: ({ signal }) =>
      request(`/api/v1/insights/team/people${search}`, { schema: TeamPeople, signal }),
    staleTime: STALE,
  });
}

// --- Saved views (X6) -----------------------------------------------------------------------------

export const savedViewsKey = (report: SavedViewReport) => ['insights', 'views', report] as const;

/** The person's own saved views of a report. Never stale for long: they change them here. */
export function savedViewsQuery(report: SavedViewReport) {
  return queryOptions({
    queryKey: savedViewsKey(report),
    queryFn: ({ signal }) =>
      request(`/api/v1/insights/views?report=${report}`, { schema: SavedViewList, signal }),
  });
}

export function createSavedView(body: SavedViewCreate) {
  return request('/api/v1/insights/views', { method: 'POST', body, schema: SavedView });
}

export function updateSavedView(id: string, body: SavedViewUpdate) {
  return request(`/api/v1/insights/views/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body,
    schema: SavedView,
  });
}

export function removeSavedView(id: string) {
  return request(`/api/v1/insights/views/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    schema: SavedViewList,
  });
}

/** A register view as a saved view's query: the address's own names, no paging. */
export function savedQueryOf(query: Loose<RegisterQuery>): Record<string, string> {
  return Object.fromEntries(
    new URLSearchParams(registerSearch({ ...query, page: undefined, size: undefined })),
  );
}
